import { useState } from "react";
import { motion } from "framer-motion";
import { BRAND } from "@/styles/brand";

const C = BRAND.colors;

const STYLES = {
  current: { name: "Aktuell (SVG)", description: "Die jetzigen handgezeichneten SVG-Illustrationen" },
  ag: { name: "Stil A+G (Ben & Jerry's + Collage)", description: "Verspielte Charaktere auf Craft-Paper Collagen" },
  editorial: { name: "Editorial (Magazine)", description: "Premium Food-Magazin Flat-Lay Stil" },
};

const COMPARISONS = [
  {
    label: "Landing Hero",
    current: null, // SVG inline - no image
    ag: "/style-ag/hero-landing.png",
    editorial: "/app-v2/hero-landing.png",
  },
  {
    label: "Tile: Tisch Buchen",
    current: null,
    ag: "/style-ag/tile-tisch.png",
    editorial: "/app-v2/tile-tisch.png",
  },
  {
    label: "Tile: Coupons",
    current: null,
    ag: "/style-ag/tile-coupons.png",
    editorial: "/app-v2/tile-coupons.png",
  },
  {
    label: "Tile: Stempelkarte",
    current: null,
    ag: "/style-ag/tile-stempel.png",
    editorial: "/app-v2/tile-stempel.png",
  },
  {
    label: "Tile: Bestellen",
    current: null,
    ag: "/style-ag/tile-bestellen.png",
    editorial: "/app-v2/tile-bestellen.png",
  },
  {
    label: "Coupon: Cappuccino",
    current: null,
    ag: "/style-ag/coupon-cappuccino.png",
    editorial: null,
  },
  {
    label: "Coupon: Pasta",
    current: null,
    ag: "/style-ag/coupon-pasta.png",
    editorial: null,
  },
  {
    label: "Coupon: Menu Deal",
    current: null,
    ag: "/style-ag/coupon-menu.png",
    editorial: null,
  },
  {
    label: "Auth: Login",
    current: "/auth/auth-welcome.png",
    ag: "/style-ag/auth-welcome.png",
    editorial: null,
  },
  {
    label: "Auth: Register",
    current: "/auth/auth-register.png",
    ag: "/style-ag/auth-register.png",
    editorial: null,
  },
  {
    label: "Auth: Verify",
    current: "/auth/auth-verify.png",
    ag: "/style-ag/auth-verify.png",
    editorial: null,
  },
  {
    label: "Auth: Success",
    current: "/auth/auth-success.png",
    ag: "/style-ag/auth-success.png",
    editorial: null,
  },
  {
    label: "Leerer Warenkorb",
    current: null,
    ag: "/style-ag/empty-cart.png",
    editorial: "/app-v2/empty-cart.png",
  },
  {
    label: "Bestellung Erfolg",
    current: null,
    ag: "/style-ag/order-success.png",
    editorial: "/app-v2/order-success.png",
  },
  {
    label: "404 Error",
    current: null,
    ag: "/style-ag/error-404.png",
    editorial: "/app-v2/error-404.png",
  },
];

export default function StyleCompare() {
  const [activeStyle, setActiveStyle] = useState("ag");

  return (
    <div className="min-h-screen pb-28" style={{ backgroundColor: C.bg }}>
      {/* Header */}
      <div className="px-5 pt-6 pb-4">
        <h1 className="text-2xl font-bold" style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
          Stil-Vergleich
        </h1>
        <p className="text-sm mt-1" style={{ color: C.textMuted }}>
          Tippe auf einen Stil um alle Illustrationen zu sehen
        </p>
      </div>

      {/* Style Selector */}
      <div className="px-5 mb-5">
        <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
          {Object.entries(STYLES).map(([key, style]) => (
            <button key={key}
              onClick={() => setActiveStyle(key)}
              className="px-4 py-3 rounded-2xl text-[13px] font-semibold whitespace-nowrap transition-all flex-shrink-0"
              style={{
                backgroundColor: activeStyle === key ? C.green : C.bgCard,
                color: activeStyle === key ? "#fff" : C.textMuted,
                boxShadow: activeStyle === key ? `0 4px 12px ${C.green}30` : BRAND.shadows.sm,
              }}>
              {style.name}
            </button>
          ))}
        </div>
        <p className="text-xs mt-2" style={{ color: C.textMuted }}>
          {STYLES[activeStyle].description}
        </p>
      </div>

      {/* Side by Side Toggle */}
      <div className="px-5 space-y-4">
        {COMPARISONS.map((comp, i) => {
          const imgSrc = comp[activeStyle];
          return (
            <motion.div key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: C.bgCard, boxShadow: BRAND.shadows.sm }}>

              {/* Label */}
              <div className="px-4 py-2.5 flex items-center justify-between"
                style={{ borderBottom: `1px solid ${C.border}` }}>
                <span className="text-[13px] font-bold" style={{ color: C.text }}>
                  {comp.label}
                </span>
                <span className="text-[11px] px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: imgSrc ? C.green + "12" : C.border + "40",
                    color: imgSrc ? C.green : C.textMuted,
                  }}>
                  {imgSrc ? STYLES[activeStyle].name : "Nicht verfügbar"}
                </span>
              </div>

              {/* Image */}
              <div className="p-4 flex items-center justify-center" style={{ minHeight: 200 }}>
                {imgSrc ? (
                  <img src={imgSrc} alt={comp.label}
                    className="max-w-full max-h-[280px] rounded-xl object-contain"
                    style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))" }} />
                ) : (
                  <div className="text-center py-8">
                    <span className="text-4xl block mb-2">🎨</span>
                    <p className="text-sm" style={{ color: C.textMuted }}>
                      {activeStyle === "current" ? "Inline SVG - nur in der App sichtbar" : "Nicht generiert fuer diesen Stil"}
                    </p>
                  </div>
                )}
              </div>

              {/* Quick compare: show all styles for this item */}
              <div className="px-4 pb-3 flex gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
                {Object.entries(STYLES).map(([key, style]) => {
                  const src = comp[key];
                  if (!src) return null;
                  const isActive = key === activeStyle;
                  return (
                    <button key={key}
                      onClick={() => setActiveStyle(key)}
                      className="flex-shrink-0 rounded-xl overflow-hidden transition-all"
                      style={{
                        width: 64, height: 64,
                        border: isActive ? `3px solid ${C.green}` : `2px solid ${C.border}`,
                        opacity: isActive ? 1 : 0.6,
                      }}>
                      <img src={src} alt={style.name}
                        className="w-full h-full object-cover" />
                    </button>
                  );
                })}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Bottom spacer */}
      <div className="h-8" />
    </div>
  );
}
